import { BoardSection, BoardSectionType } from "shared/datamodel/schemas/board-section";
import style from "./section-projects.module.css";
import { Project } from "shared/datamodel/schemas/project";
import { ProjectCard } from "./project-card";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import { useEffect, useRef, useMemo } from "react";
import { Team } from "shared/datamodel/schemas/team";
import { useTeamsCache } from "frontend/hooks/caching/use-teams-cache";
import { CanvasTemplate } from "shared/datamodel/schemas";
import { useTeamsWithProjects } from "frontend/hooks/caching/use-teams-with-projects";
import { PlusIcon } from "frontend/icons/plus";
import { CreateProject } from "./side-menu/create-project";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import useSafeState from "frontend/hooks/use-safe-state";
import useStateValue from "frontend/state/value";
import RestrictedUserModal from "frontend/modals/restricted-user-new-modal";
import Modal from "frontend/modal/modal";

export default function SectionProjects({
  section,
  createNewBoard,
  onEditSeats,
  setSelectedProject,
  setIsMovingBoards,
}: {
  section: BoardSection;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onEditSeats: () => void;
  setSelectedProject: (projectId: string) => void;
  setIsMovingBoards: (value: boolean) => void;
}) {
  const [projects, setProjects] = useSafeState<Project[]>([]);
  const [parentTeam, setParentTeam] = useSafeState<Team | null>(null);
  const [showCreateProjectModal, setShowCreateProjectModal] = useSafeState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useSafeState(false);
  const [{ user }] = useStateValue();
  const { getProjects } = useProjectsCache();
  const { getTeams } = useTeamsCache();
  const { teamsWithProjects } = useTeamsWithProjects();
  const [projectsOverflow, setProjectsOverflow] = useSafeState(0);
  const [showAllProjects, setShowAllProjects] = useSafeState(false);
  const teamsWithProjectsArray = teamsWithProjects ?? [];

  const gridRef = useRef<HTMLDivElement | null>(null);
  const showMoreLessRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const team = teamsWithProjectsArray.find((team) => team.documentId === section.id);
        if (!team) {
          setProjects([]);
          setParentTeam(null);
          return;
        }
        const [fetchedProjects, parentTeams] = await Promise.allSettled([
          team.projects?.length > 0 && getProjects(team.projects.map((project) => project.documentId)),
          getTeams([team.documentId]),
        ]);
        if (fetchedProjects.status === "fulfilled" && Array.isArray(fetchedProjects.value)) {
          setProjects(fetchedProjects.value);
        }
        if (parentTeams.status === "fulfilled") {
          setParentTeam(parentTeams.value[0]);
        }
      } catch {
        setProjects([]);
        setParentTeam(null);
      }
    };

    fetchProjects();
  }, [section.id, getProjects, getTeams, teamsWithProjects]);

  useEffect(() => {
    const calculateAndPosition = () => {
      if (!gridRef.current) return;
      const gridWidth = gridRef.current.offsetWidth;
      const minProjectCardWidth = 204; // Minimum width of a project card
      const gridGap = 20; // Minimum gap between items
      const totalCardWidth = minProjectCardWidth + gridGap; // Total width including gap

      const projectsPerRow = Math.floor((gridWidth + gridGap) / totalCardWidth); // The last column has no gap, we factor that in
      const numberOfVisibleRows = projectsPerRow > 2 ? 2 : 3;

      const newOverflow = projects.length + 1 - numberOfVisibleRows * projectsPerRow;
      setProjectsOverflow(newOverflow);

      // Determine the position of the showMoreLessRef
      if (!showMoreLessRef.current) return;
      const lastVisibleCardIndex = Math.min(projects.length, projectsPerRow) - 1;
      const lastCard = gridRef.current.children[lastVisibleCardIndex] as HTMLElement;
      if (lastCard) {
        const lastCardRightEdge = lastCard.offsetLeft + lastCard.offsetWidth;
        const spanWidth = showMoreLessRef.current.offsetWidth;
        showMoreLessRef.current.style.position = "absolute";
        showMoreLessRef.current.style.left = `${lastCardRightEdge - spanWidth}px`;
      }
    };

    calculateAndPosition();
    window.addEventListener("resize", calculateAndPosition);

    return () => {
      window.removeEventListener("resize", calculateAndPosition);
    };
  }, [projects.length]);

  const memoizedProjects = useDeepEqualMemo(projects);
  const visibleProjects = useMemo(() => {
    if (showAllProjects) return memoizedProjects;
    if (projectsOverflow === 0) return memoizedProjects;
    const showUntil = memoizedProjects.length - projectsOverflow;
    return memoizedProjects.slice(0, showUntil);
  }, [showAllProjects, projectsOverflow, memoizedProjects]);

  if (section.type !== BoardSectionType.WORKSPACE) {
    return null;
  }

  const renderProject = (project: Project) => {
    if (!parentTeam) {
      return null;
    }
    return (
      <ProjectCard
        setSelectedProject={setSelectedProject}
        project={project}
        parentTeam={parentTeam}
        createNewBoard={createNewBoard}
        onEditSeats={onEditSeats}
        key={project.documentId}
      />
    );
  };

  return (
    <>
      <div className={style.header}>
        <span className={style.title}>PROJECTS</span>
        {projectsOverflow > 0 && (
          <span
            ref={showMoreLessRef}
            className={style.showMoreLess}
            onClick={() => setShowAllProjects(!showAllProjects)}
          >
            {showAllProjects ? "Show less projects" : "Show all projects"}
          </span>
        )}
      </div>
      <div>
        <div ref={gridRef} className={style.projectsGrid}>
          <div
            className={style.addProjectContainer}
            onClick={() => {
              const shouldShoWRestrictedModal = !user?.isSubscribedToCanvas;
              if (shouldShoWRestrictedModal) {
                setShowRestrictedModal(true);
              } else {
                setShowCreateProjectModal(true);
              }
            }}
            data-testid="project-section-add-project-button"
          >
            <div className={style.addProject}>
              <PlusIcon color="#1973FF" size={9} />
              <div className={style.addProjectText}>Add Project</div>
            </div>
          </div>
          {visibleProjects.map((item) => renderProject(item))}
        </div>
      </div>
      {showCreateProjectModal && section.id && (
        <CreateProject
          parentTeamId={section.id}
          createNewBoard={createNewBoard}
          onEditSeats={onEditSeats}
          setSelectedProject={setSelectedProject}
          onDismiss={() => setShowCreateProjectModal(false)}
          setIsMovingBoards={setIsMovingBoards}
        />
      )}
      {showRestrictedModal && (
        <Modal dimBackground={true}>
          <RestrictedUserModal trackingEvent={"home"} onDismiss={() => setShowRestrictedModal(false)} />
        </Modal>
      )}
      <div className={style.title}>CANVASES</div>
    </>
  );
}
